import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HourglassFullIcon from '@mui/icons-material/HourglassFull'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { addMinutes } from 'date-fns'
import {
  ProcessedEvent,
  getColorByColorName,
  getColorById,
  getTaskStatusIcon,
  renderScenario,
} from 'pages/TaskScheduler/TaskSchedulerUtils'
import React from 'react'
import { PATH } from 'utils/constants'
import { VENDOR } from 'utils/vendorDisplayName'

import type { FilterableRecord } from './filterableRecord'

import schedulerStyle from 'pages/TaskScheduler/SchedulerSettings.module.css'

/**
 * Read-only calendar events from normalized statistics rows.
 */
export function filterableRecordToProcessedEvent(
  r: FilterableRecord,
  robotColorBySn?: Record<string, string | null | undefined>,
  robotName?: string
): ProcessedEvent {
  const end = r.endAt ?? addMinutes(r.startAt, 30)
  let fallbackKey = 0
  for (let i = 0; i < r.robotSn.length; i++) fallbackKey += r.robotSn.charCodeAt(i)
  const colorKey = r.robotId && r.robotId > 0 ? r.robotId : Math.abs(fallbackKey % 1000)
  const robotColorName = robotColorBySn ? robotColorBySn[r.robotSn] : undefined
  const eventColor = robotColorName ? getColorByColorName(robotColorName) : getColorById(colorKey)

  const isStale = Date.now() - r.startAt.getTime() > 24 * 60 * 60 * 1000
  const statusPrefix = r.cancellationReason
    ? [7, 8, 9, 10].includes(r.executionStatus as number)
      ? getTaskStatusIcon(r.executionStatus, isStale)
      : React.createElement(StopCircleOutlinedIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
    : r.executionStatus === 99
    ? null
    : r.vendor === VENDOR.GAUSIUM
    ? r.endAt
      ? React.createElement(TaskAltIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      : isStale
      ? React.createElement(HelpOutlineIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
      : React.createElement(HourglassFullIcon, { style: { fontSize: '1.3em' }, color: 'inherit' })
    : getTaskStatusIcon(r.executionStatus, isStale)

  const rawTaskIdPart = r.taskId != null ? String(r.taskId) : r.sourceEventId
  const taskIdPart =
    r.vendor === VENDOR.GAUSIUM &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(rawTaskIdPart)
      ? rawTaskIdPart.split('-')[0]
      : rawTaskIdPart

  const title = r.displayName || `Task ${rawTaskIdPart}`

  const description = React.createElement(
    React.Fragment,
    {},
    React.createElement(
      'div',
      {},
      React.createElement('span', { style: { fontWeight: 'bold' } }, 'Task: '),
      taskIdPart
    ),
    React.createElement(
      'div',
      {},
      React.createElement('span', { style: { fontWeight: 'bold' } }, 'Robot: '),
      React.createElement(
        'a',
        {
          className: schedulerStyle.link,
          href: PATH.ROBOT + (r.robotId || r.robotSn),
          target: '_blank',
          rel: 'noreferrer',
        },
        robotName || r.robotSn
      )
    ),
    r.scenarioId ? renderScenario(r.scenarioId) : null,

    r.vendor === VENDOR.GAUSIUM && r.mapId
      ? React.createElement(
          'div',
          { style: { marginBottom: '2px' } },
          React.createElement('span', { style: { fontWeight: 'bold' } }, 'Map: '),
          String(r.mapId)
        )
      : null
  )

  const ev: ProcessedEvent = {
    event_id: `norm:${r.sourceEventId}`,
    title: title as any,
    start: r.startAt,
    end,
    color:
      r.executionStatus === 10
        ? '#ef4444'
        : (r.cancellationReason || r.executionStatus === 99) && r.executionStatus !== 8
        ? 'gray'
        : eventColor,
    description,
  }

  ev.extendedProps = {
    statusPrefix,
    vendor: r.vendor,
    robot_sn: r.robotSn,
    robot_id: r.robotId,
    facility_ids: r.facilityIds,
    vendor_origin_record_type: r.vendorOriginRecordType ?? null,
    source_event_id: r.sourceEventId,
    mission_id: r.missionId,
    scenario: r.scenarioId,
    missions:
      Array.isArray((r.overallProgress as any)?.missions) && (r.overallProgress as any).missions.length > 0
        ? (r.overallProgress as any).missions
        : r.missionsInfoIds,
    missions_info: r.missionsInfo,
    map_id: r.mapId,
    normalized: true,
    task_id: r.taskId,
    task_type: r.vendorOriginRecordType === 'odyssey_event_statistics' ? 'Odyssey' : 'Task',
    mission_name: r.displayName,
    mission_status: r.executionStatus,
    overall_progress: r.overallProgress || undefined,
    missions_progress: Array.isArray(r.missionsProgress) ? r.missionsProgress : [],
    exception_reason: r.cancellationReason,
    exception_user: r.cancellationUser,
    status: r.cancellationReason ? 'deleted' : 'executed',
  }

  return ev
}
